import { format } from 'date-fns';

import { Formik, FormikHelpers } from 'formik';
import _ from 'lodash';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { TeamPlayerForm } from 'modules/teams/ui/components/team-card/styles';
import { useState } from 'react';
import { usePreventNavigation } from 'shared/hooks';
import { birthDateSchema } from 'shared/validators/date.validator';
import { DefaultDropzone } from 'ud-form/components/dropzoneV2';
import UDFormInput from 'ud-form/components/input';
import { birthDateFormatter } from 'ud-form/components/input/domain/formatters/birth-date-formatter';
import UDFormLabel from 'ud-form/components/label';
import UDButton from 'ud-ui/components/button';
import * as yup from 'yup';
import { Player } from 'modules/players/domain/interfaces/player';

interface FormValues {
  id?: number;
  surname: string;
  name: string;
  middleName?: string;
  birthDate: string;
  height?: string;
  weight?: string;
  photo?: (File | string)[];
}

export type SubmitPlayerForm = ({ values, formikHelpers }: { values: FormValues; formikHelpers: FormikHelpers<FormValues> }) => void;

interface FormProps {
  onSubmit: SubmitPlayerForm;
  initialValues?: Player;
  isLoading?: boolean;
}

export const teamPlayerValidator = yup.object().shape({
  name: yup.string().required('Обязательное поле'),
  surname: yup.string().required('Обязательное поле'),
  birthDate: birthDateSchema,
});

const PlayerForm = (props: FormProps) => {
  const { onSubmit, initialValues: initialPlayer, isLoading: isLoadingForm } = props;

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [leavingConfirmed, setLeavingConfirmed] = useState(false);

  const { backHandler, PreventNavigationModal } = usePreventNavigation({
    shouldPrevent: isFormDirty && !leavingConfirmed,
    onConfirm: () => {
      setLeavingConfirmed(true);
      setIsFormDirty(false);
    },
    defaultPath: Pages.PLAYERS.INDEX,
  });

  const initialValues: FormValues = initialPlayer
    ? {
        id: initialPlayer.id,
        surname: initialPlayer.surname,
        name: initialPlayer.name,
        middleName: initialPlayer.middleName,
        birthDate: initialPlayer.birthDate ? format(new Date(initialPlayer.birthDate), 'dd.MM.yyyy') : '',
        height: initialPlayer.height?.toString(),
        weight: initialPlayer.weight?.toString(),
        photo: initialPlayer.photo ? [initialPlayer.photo.externalUrl] : [],
      }
    : {
        surname: '',
        name: '',
        middleName: '',
        birthDate: '',
      };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={teamPlayerValidator}
      onSubmit={(values, formikHelpers) => {
        onSubmit({ values, formikHelpers });
        formikHelpers.resetForm({ values }); // делаем текущие значения базой
        setIsFormDirty(false);
      }}
      validate={(values) => {
        const dirty = !_.isEqual(values, initialValues);
        if (dirty !== isFormDirty && !leavingConfirmed) {
          setIsFormDirty(dirty);
        }
        return {};
      }}
    >
      {(formProps) => {
        return (
          <form className="flex h-full flex-col justify-between" onSubmit={formProps.handleSubmit}>
            <TeamPlayerForm>
              <div className="row">
                <div className="w-1/2" style={{ paddingRight: 24 }}>
                  <UDFormInput name="surname" label="Фамилия" placeholder="Введите фамилию" />

                  <div className="player-data">
                    <UDFormInput name="name" label="Имя" placeholder="Введите имя" />

                    <UDFormInput name="middleName" label="Отчество" placeholder="Введите отчество" />
                  </div>

                  <div className="player-data">
                    <UDFormInput
                      name="birthDate"
                      label="Дата рождения"
                      placeholder="Укажите дату рождения"
                      mask="дд.мм.гггг"
                      formatter={birthDateFormatter}
                      parser={birthDateFormatter}
                    />
                  </div>

                  <div className="player-data">
                    <UDFormInput name="height" label="Рост игрока" placeholder="Укажите рост" />

                    <UDFormInput name="weight" label="Вес игрока" placeholder="Укажите вес" />
                  </div>
                </div>

                <div className="w-1/2" style={{ paddingLeft: 24 }}>
                  <UDFormLabel className="mb-2">Фото игрока</UDFormLabel>
                  <DefaultDropzone name="photo" className="p-6" />
                </div>
              </div>

              <div className="form-buttons w-1/2 pr-6">
                <UDButton
                  type="submit"
                  variant="primary"
                  disabled={isLoadingForm || !formProps.isValid || formProps.isSubmitting}
                  loading={isLoadingForm || formProps.isSubmitting}
                  className="mr-4 w-1/2"
                >
                  Сохранить
                </UDButton>

                <UDButton type="button" variant="secondary" disabled={isLoadingForm} onClick={backHandler} className="w-1/2">
                  Отменить
                </UDButton>
              </div>
            </TeamPlayerForm>

            <PreventNavigationModal />
          </form>
        );
      }}
    </Formik>
  );
};

export default PlayerForm;
